import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Calendar, History, Disc3, Music, Tv, Globe, ArrowRight, Sun, Moon,
} from "lucide-react";
import axios from "axios";
import Tx from "@/components/Tx";
import { useAuth } from "@/context/AuthContext";
import { SONGS, ALBUMS, EPS } from "@/data/eiffel65";
import { E65_TV_PLAYLIST } from "@/data/e65_tv";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =================================================================
 * HOME DASHBOARD — Today-In-History · Pick of the Day · Live Stats · Greeting
 * Deterministic daily picks (no backend changes needed):
 *   day-of-year + year → seeded RNG → reproducible song/album/video index.
 * Translations: every label/sentence wrapped in <Tx>.
 * Eigennamen (Songtitel/Albumtitel/Eigennamen) bleiben raw — Tx wird vermieden.
 * ============================================================== */

// Deterministic pick: same item for everyone on the same day.
function dailyIndex(seed, len) {
  if (!len) return 0;
  const d = new Date();
  const dayKey = d.getFullYear() * 1000 + Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
  let h = (dayKey ^ seed) >>> 0;
  h = (h * 2654435761) >>> 0;
  return h % len;
}

// "Today in History": find releases where month-day matches today (rough match by year-only data).
function todayInHistory() {
  const today = new Date();
  const year = today.getFullYear();
  const out = [];
  for (const a of [...ALBUMS, ...EPS]) {
    if (!a.year || a.year >= year) continue;
    const diff = year - a.year;
    if (diff > 0 && diff % 1 === 0) out.push({ kind: "album", title: a.title, year: a.year, diff, cover: a.cover, id: a.id });
  }
  for (const s of SONGS) {
    if (!s.year || s.year >= year) continue;
    const diff = year - s.year;
    if (diff > 0) out.push({ kind: "song", title: s.title, year: s.year, diff, id: s.id });
  }
  // Pick one deterministic for today
  if (!out.length) return null;
  return out[dailyIndex(0xA001, out.length)];
}

function GreetingHeader({ user }) {
  const hour = new Date().getHours();
  const isMorning = hour < 12;
  const isAfternoon = hour >= 12 && hour < 18;
  const greeting = isMorning ? "Good morning" : isAfternoon ? "Good afternoon" : "Good evening";
  const displayName = user?.username || user?.email?.split("@")[0] || null;
  return (
    <div data-testid="home-greeting" className="flex items-center gap-3 mb-4">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#00AFFF]/15 border border-[#00AFFF]/40 text-[#00AFFF]">
        {isMorning ? <Sun size={18} /> : isAfternoon ? <Sun size={18} /> : <Moon size={18} />}
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] font-bold"><Tx>The Fan Community</Tx></div>
        <div className="font-display font-black text-white text-lg sm:text-xl">
          <Tx>{greeting}</Tx>{displayName ? <span className="text-[#00AFFF]">, {displayName}</span> : <span>!</span>}
        </div>
      </div>
    </div>
  );
}

function DailyCard({ icon, kicker, title, subtitle, to, accent = "#00AFFF", testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative glass glass-hover rounded-2xl p-4 sm:p-5 flex flex-col gap-3 overflow-hidden h-full"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-30 group-hover:opacity-60 transition" style={{ background: accent }} />
      <div className="relative flex items-start justify-between">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border" style={{ background: `${accent}1a`, borderColor: `${accent}55`, color: accent }}>
          {icon}
        </span>
        <ArrowRight size={14} className="text-white/35 group-hover:text-[#00AFFF] group-hover:translate-x-1 transition" />
      </div>
      <div className="relative">
        <div className="text-[9px] uppercase tracking-[0.35em] font-bold mb-1" style={{ color: accent }}><Tx>{kicker}</Tx></div>
        <div className="font-display font-black text-white text-base sm:text-lg leading-tight break-words">{title}</div>
        {subtitle && <div className="text-[11px] sm:text-xs text-white/55 mt-1.5 font-body">{subtitle}</div>}
      </div>
    </Link>
  );
}

export default function HomeDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ users: 0, news: 0, posts: 0, comments: 0 });

  // Deterministic daily picks (memoized — same for everyone today)
  const dailySong = useMemo(() => SONGS[dailyIndex(0x5301, SONGS.length)], []);
  const dailyAlbum = useMemo(() => ALBUMS[dailyIndex(0x5302, ALBUMS.length)], []);
  const dailyVideo = useMemo(() => E65_TV_PLAYLIST[dailyIndex(0x5303, E65_TV_PLAYLIST.length)], []);
  const flashback = useMemo(() => todayInHistory(), []);

  // Fetch live stats from existing /api/stats (already used by LiveStats)
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/stats`).then((r) => {
      if (alive) setStats(r.data || {});
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const accentBySection = {
    song: "#00AFFF",
    album: "#A78BFA",
    video: "#F472B6",
    flashback: "#FBBF24",
  };

  return (
    <section data-testid="home-dashboard" className="container mx-auto max-w-[1500px] px-4 sm:px-8 pt-10 sm:pt-14">
      <GreetingHeader user={user} />

      {/* Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
        {[
          { v: stats.users || 0, label: "Members", icon: <Globe size={14} /> },
          { v: stats.news || 0, label: "News articles", icon: <Sparkles size={14} /> },
          { v: stats.posts || 0, label: "Forum posts", icon: <History size={14} /> },
          { v: SONGS.length + ALBUMS.length + EPS.length, label: "Releases on file", icon: <Disc3 size={14} /> },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: i * 0.05 }}
            data-testid={`home-stat-${s.label.toLowerCase().split(' ')[0]}`}
            className="glass rounded-xl p-3 flex items-center gap-2.5"
          >
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-[#00AFFF]/15 text-[#00AFFF] shrink-0">{s.icon}</span>
            <div className="min-w-0">
              <div className="font-display font-black text-white text-lg sm:text-xl tabular-nums leading-none">{s.v.toLocaleString()}</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-white/45 mt-0.5"><Tx>{s.label}</Tx></div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pick-of-the-Day Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {dailySong && (
          <DailyCard
            testid="home-song-of-day"
            icon={<Music size={18} />}
            kicker="Song of the day"
            title={dailySong.title}
            subtitle={<span><Tx>From</Tx> · {dailySong.album} · {dailySong.year}</span>}
            to={`/discography?song=${dailySong.id}`}
            accent={accentBySection.song}
          />
        )}
        {dailyAlbum && (
          <DailyCard
            testid="home-album-of-day"
            icon={<Disc3 size={18} />}
            kicker="Album of the day"
            title={dailyAlbum.title}
            subtitle={<span>{dailyAlbum.year} · <Tx>{dailyAlbum.label}</Tx></span>}
            to={`/discography?album=${dailyAlbum.id}`}
            accent={accentBySection.album}
          />
        )}
        {dailyVideo && (
          <DailyCard
            testid="home-video-of-day"
            icon={<Tv size={18} />}
            kicker="Video of the day"
            title={dailyVideo.title}
            subtitle={<span>{dailyVideo.year} · <Tx>{dailyVideo.category}</Tx></span>}
            to={`/videos?ch=${dailyVideo.id}`}
            accent={accentBySection.video}
          />
        )}
        {flashback && (
          <DailyCard
            testid="home-flashback"
            icon={<Calendar size={18} />}
            kicker={`${flashback.diff} years ago today`}
            title={flashback.title}
            subtitle={<span><Tx>Released in</Tx> {flashback.year}</span>}
            to={flashback.kind === "album" ? `/discography?album=${flashback.id}` : `/discography?song=${flashback.id}`}
            accent={accentBySection.flashback}
          />
        )}
      </div>
    </section>
  );
}
